import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  MapPin, Navigation, Compass, Route, Flag, ArrowRight, Phone,
  Building2, User, Briefcase, Car, CheckCircle, Clock, Star, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════
   GPS-Themed Route Definitions
   ═══════════════════════════════════════════════════════════ */
const ROUTES = [
  {
    id: 'builder',
    suit: '\u2660', suitColor: 'text-white',
    icon: Compass,
    label: 'Route A',
    name: "The Builder's Route",
    tagline: 'Investor / Entrepreneur',
    who: 'I want to build a business in auto & transportation.',
    color: 'from-amber-500 to-orange-600',
    accent: 'amber',
    description: 'You have a vision to build, invest in, or scale a transportation business. Whether you want to launch a black car service, build a fleet, or enter as a passive investor — this route gives you the roadmap, business credit strategy, and operational blueprint to get there.',
    deliverables: [
      'Discovery Session + Written Roadmap',
      'Entity & EIN Review',
      'Business Credit Assessment & Action Plan',
      'Pricing Structure Build',
      'Corporate Account Strategy',
      'Fleet Acquisition Planning',
      'Driven Collective Access Discussion',
    ],
    timeline: [
      { phase: 'Days 1–30', title: 'Foundation', items: 'Discovery Session, entity/EIN review, business credit assessment, priority phase identified' },
      { phase: 'Days 31–60', title: 'Build', items: 'Credit action plan executing, first tradelines, pricing structure built, corporate outreach initiated' },
      { phase: 'Days 61–90', title: 'Traction', items: 'First bookings/clients, fleet strategy confirmed, Driven Collective affiliate discussed, 90-day review' },
    ],
  },
  {
    id: 'personal',
    suit: '\u2666', suitColor: 'text-red-500',
    icon: Navigation,
    label: 'Route B',
    name: 'The Personal Lane',
    tagline: 'Individual / Personal',
    who: 'I want to upgrade how I move through the world.',
    color: 'from-sky-500 to-blue-600',
    accent: 'sky',
    description: "Your auto lifestyle isn't matching your ambitions. Maybe you're overpaying, driving the wrong vehicle, or you're ready for a concierge-level experience. This route transforms how you move — from vehicle acquisition to personal driver matchmaking.",
    deliverables: [
      'Personal Auto Lifestyle Assessment',
      'Vehicle Analysis & Acquisition Strategy',
      'Credit Optimization for Better Access',
      'Concierge Driver Matchmaking',
      'Membership Tier Activation',
      'Lifestyle Check-in Sessions',
    ],
    timeline: [
      { phase: 'Days 1–30', title: 'Assessment', items: 'Discovery Session, vehicle analysis, acquisition strategy defined, membership tier confirmed' },
      { phase: 'Days 31–60', title: 'Transformation', items: 'Vehicle transition in progress, personal driver engaged if applicable, lifestyle check-in completed' },
    ],
  },
  {
    id: 'corporate',
    suit: '\u2663', suitColor: 'text-white',
    icon: Building2,
    label: 'Route C',
    name: 'The Executive Corridor',
    tagline: 'Corporate / Executive',
    who: 'I need auto lifestyle solutions for my team & events.',
    color: 'from-emerald-500 to-green-600',
    accent: 'emerald',
    description: "Your organization needs a professional ground transportation program — not ad hoc Uber rides. This route builds a corporate transport policy, activates ACE as your preferred provider, and delivers white-glove service for your executives, clients, and events.",
    deliverables: [
      'Corporate Transport Audit',
      'Vendor Assessment & Benchmarking',
      'Travel Policy Framework',
      'Preferred Provider Activation',
      'Event & Conference Transport Planning',
      'Ongoing Account Management',
    ],
    timeline: [
      { phase: 'Days 1–30', title: 'Audit', items: 'Discovery Session, transport audit, vendor assessment, travel policy framework drafted' },
      { phase: 'Days 31–90', title: 'Activation', items: 'Policy finalized, ACE activated as preferred provider, first managed transport completed, review' },
    ],
  },
  {
    id: 'accelerator',
    suit: '\u2665', suitColor: 'text-red-500',
    icon: Flag,
    label: 'Route D',
    name: 'The Accelerator',
    tagline: 'Driver → Owner-Operator',
    who: 'I want to stop driving for others and build my own operation.',
    color: 'from-red-500 to-rose-600',
    accent: 'red',
    description: "You're behind the wheel for someone else's platform. This is the most structured route — the ACCD Certification program takes you from driver to owner-operator with an 8-module curriculum, business credit building, and a clear path to your first independent bookings.",
    deliverables: [
      'ACCD Certification (8 Modules)',
      '6-Phase Owner-Operator Roadmap',
      'Business Entity & Credit Setup',
      'Pricing & Service Standards Training',
      'Corporate Account Landing Strategy',
      'Driver Network & Fleet Scaling',
      '90-Day Check-in Session',
    ],
    timeline: [
      { phase: 'Week 1', title: 'Discovery + Access', items: 'Discovery Session, ACCD curriculum granted, Modules 1–2 assigned' },
      { phase: 'Weeks 2–4', title: 'Modules 1–4', items: 'Mindset, Business Foundation, Business Credit, Service Standards' },
      { phase: 'Weeks 5–7', title: 'Modules 5–8', items: 'Pricing, Corporate Accounts, Driver Network, Fleet Scaling' },
      { phase: 'Week 8', title: 'Certification', items: 'ACCD review, digital certificate issued, Driven Collective activated' },
      { phase: 'Day 90', title: 'Check-In', items: '30-min follow-up with your ACE consultant, progress review, Phase 2–4 calibration' },
    ],
    isCertification: true,
  },
];

const MEMBERSHIPS = [
  { tier: 'Lobby', price: 97, tagline: 'Self-guided access + community', features: ['Resource library access', 'Community network', 'Monthly group call', 'ACE rate card'] },
  { tier: 'Condo', price: 397, tagline: '2 sessions/month + direct messaging', features: ['Everything in Lobby', '2 private sessions/mo', 'Direct messaging to ACE team', 'Priority scheduling', 'Custom action plans'] },
  { tier: 'Penthouse', price: 1200, tagline: 'Full concierge consulting', features: ['Everything in Condo', 'Unlimited sessions', 'ACE embedded in your build', 'Corporate introductions', 'Revenue share eligible', 'First priority on all ACE resources'] },
];

const PROCESS_STEPS = [
  { icon: MapPin, title: 'Pin Your Location', desc: 'Complete your intake profile so we know exactly where you stand today.' },
  { icon: Navigation, title: 'Set Your Destination', desc: 'Discovery Session with ACE — define your goals and where you want to go.' },
  { icon: Route, title: 'Map Your Route', desc: 'Receive your custom roadmap with phases, milestones, and action items.' },
  { icon: Flag, title: 'Drive', desc: 'Execute with ACE support. Build, transition, and arrive at your destination.' },
];

/* ═══════════════════════════════════════════════════════════ */

const DrivingServices = () => {
  const navigate = useNavigate();
  const [expandedRoute, setExpandedRoute] = useState(null);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="driving-services-page">

      {/* ── HERO ── */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-6">Auto Lifestyle Consulting</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6">
            You're Driven.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-amber-500">We're Driving.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Whether you're building a transportation empire, upgrading your personal auto lifestyle, or need executive ground solutions — ACE maps the route and rides with you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/consultation/intake')} className="bg-red-600 hover:bg-red-700 text-white h-12 px-8 text-sm font-bold" data-testid="start-profile-btn">
              <MapPin className="h-4 w-4 mr-2" /> Start Your Profile
            </Button>
            <Button onClick={() => document.getElementById('routes')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-gray-700 text-white hover:bg-gray-900 h-12 px-8 text-sm font-bold" data-testid="explore-routes-btn">
              Explore the Routes <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── ACE CARD DECK — VEHICLE ACQUISITION ── */}
      <section id="acquisition" className="py-20 border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase text-center mb-3">Pick Your Card</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">Vehicle Acquisition</h2>
          <p className="text-gray-500 text-center max-w-lg mx-auto mb-12 text-sm">ACE connects you to the right vehicle through the right path — whether you want to buy, rent, or build toward ownership.</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                suit: '\u2660', suitColor: 'text-white', icon: Car, title: 'Sales', color: 'from-emerald-500 to-green-600',
                desc: 'Buy outright through ACE\'s vetted network. We source, negotiate, and deliver — you just drive.',
                features: ['Auction access & dealer network', 'Vehicle history & inspection', 'Negotiation & acquisition strategy', 'Financing coordination'],
                cta: 'Explore Sales', route: '/sales',
              },
              {
                suit: '\u2666', suitColor: 'text-red-500', icon: Compass, title: 'Rental', color: 'from-amber-500 to-orange-600',
                desc: 'Self-drive access to exotic, luxury, and performance vehicles. ACE members save 15% on every rental.',
                features: ['Exotic & luxury fleet access', '15% member discount on all vehicles', 'Daily, weekend, & weekly rates', 'Flexible pickup & delivery'],
                cta: 'View Rental Fleet', route: '/rentals',
              },
              {
                suit: '\u2663', suitColor: 'text-white', icon: Route, title: 'Lease to Own', color: 'from-sky-500 to-blue-600',
                desc: 'A structured path from renting to ownership. Build equity in a vehicle while you drive it for your business.',
                features: ['Structured payment plan', 'Build equity every month', 'Credit-building pathway', 'Transition to full ownership'],
                cta: 'Start Consultation', route: '/consultation/intake',
              },
            ].map((path, i) => (
              <Card key={i} className="bg-gray-950 border-gray-800 overflow-hidden group hover:border-gray-600 transition-all text-white relative" data-testid={`card-${path.title.toLowerCase().replace(/ /g, '-')}`}>
                {/* Card suit watermark */}
                <div className="absolute top-4 right-4 opacity-10 text-6xl font-serif select-none">{path.suit}</div>
                <div className="p-6 relative z-10">
                  {/* Suit + title */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-3xl font-serif ${path.suitColor}`}>{path.suit}</span>
                    <div>
                      <h3 className="text-lg font-bold">{path.title}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{path.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {path.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-gray-400">
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => navigate(path.route)} className="w-full bg-red-600 hover:bg-red-700 text-xs font-bold" data-testid={`cta-${path.title.toLowerCase().replace(/ /g, '-')}`}>
                    {path.cta} <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="py-20 border-t border-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase text-center mb-3">The Process</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-14">How ACE Consulting Works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PROCESS_STEPS.map((step, i) => (
              <div key={i} className="relative group" data-testid={`process-step-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center shrink-0">
                    <step.icon className="h-5 w-5 text-red-500" />
                  </div>
                  <span className="text-[10px] font-bold text-gray-600 tracking-widest uppercase">Step {i + 1}</span>
                </div>
                <h3 className="text-base font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                {i < PROCESS_STEPS.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-5 -right-5 h-4 w-4 text-gray-800" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ROUTES ── */}
      <section id="routes" className="py-20 border-t border-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase text-center mb-3">Choose Your Route</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">Four Routes. One Destination.</h2>
          <p className="text-gray-500 text-center max-w-xl mx-auto mb-14 text-sm">Every ACE engagement starts with a paid Discovery Session. Select the route that matches where you're headed.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {ROUTES.map((route) => {
              const isExpanded = expandedRoute === route.id;
              return (
                <Card
                  key={route.id}
                  className={`bg-gray-950 border transition-all duration-300 overflow-hidden text-white ${isExpanded ? 'border-red-600 md:col-span-2' : 'border-gray-800 hover:border-gray-700'}`}
                  data-testid={`route-card-${route.id}`}
                >
                  <div className="p-6 relative">
                    {/* Card suit watermark */}
                    <div className="absolute top-4 right-4 opacity-10 text-5xl font-serif select-none">{route.suit}</div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className={`text-3xl font-serif ${route.suitColor}`}>{route.suit}</span>
                        <div>
                          <p className="text-[10px] font-bold text-gray-600 tracking-widest uppercase">{route.label}</p>
                          <h3 className="text-lg font-bold">{route.name}</h3>
                        </div>
                      </div>
                      {route.isCertification && (
                        <span className="text-[9px] font-bold bg-red-600 text-white px-2 py-1 rounded-full tracking-wider uppercase">ACCD Cert</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 italic mb-3">"{route.who}"</p>
                    <p className="text-sm text-gray-500 leading-relaxed mb-4">{route.description}</p>

                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => setExpandedRoute(isExpanded ? null : route.id)}
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs"
                        data-testid={`route-details-${route.id}`}
                      >
                        {isExpanded ? 'Collapse' : 'View Roadmap'}
                      </Button>
                      <Button
                        onClick={() => navigate(route.id === 'accelerator' ? '/consultation/track-d' : '/consultation/intake')}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-xs"
                        data-testid={`route-cta-${route.id}`}
                      >
                        Start Discovery <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded roadmap */}
                  {isExpanded && (
                    <div className="border-t border-gray-800 p-6 bg-gray-950/50">
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="text-sm font-bold text-red-500 mb-3 uppercase tracking-wider">Deliverables</h4>
                          <ul className="space-y-2">
                            {route.deliverables.map((d, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /> {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-red-500 mb-3 uppercase tracking-wider">Timeline</h4>
                          <div className="space-y-4">
                            {route.timeline.map((t, i) => (
                              <div key={i} className="relative pl-6 border-l-2 border-gray-800">
                                <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-red-600 border-2 border-gray-950" />
                                <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">{t.phase}</p>
                                <p className="text-sm font-semibold">{t.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{t.items}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── MEMBERSHIPS ── */}
      <section className="py-20 border-t border-gray-900 bg-gray-950/50">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase text-center mb-3">Ongoing Access</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">ACE Membership Tiers</h2>
          <p className="text-gray-500 text-center max-w-lg mx-auto mb-12 text-sm">Stay plugged into the ACE ecosystem with monthly consulting access, resources, and community.</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {MEMBERSHIPS.map((m, i) => (
              <Card key={i} className={`bg-black border p-6 relative text-white ${i === 2 ? 'border-red-600' : 'border-gray-800'}`} data-testid={`membership-${m.tier.toLowerCase()}`}>
                {i === 2 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] font-bold bg-red-600 text-white px-3 py-1 rounded-full uppercase tracking-wider">Most Access</span>}
                <p className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-1">{m.tier}</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-black text-white">${m.price}</span>
                  <span className="text-sm text-gray-500">/mo</span>
                </div>
                <p className="text-xs text-gray-500 mb-5">{m.tagline}</p>
                <ul className="space-y-2 mb-6">
                  {m.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-gray-400">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/consultation/intake')} variant={i === 2 ? 'default' : 'outline'} className={`w-full text-xs ${i === 2 ? 'bg-red-600 hover:bg-red-700' : 'border-gray-700 text-gray-300 hover:bg-gray-900'}`}>
                  Get Started
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── YOUR AUTO CONCIERGE ── */}
      <section className="py-20 border-t border-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-3">Your Concierge Team</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Auto Concierge Enterprises</h2>
          <p className="text-sm text-gray-500 mb-2">Built from the ground up — by operators, for operators.</p>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto leading-relaxed mb-8">
            ACE was built from the ground up — starting from behind the wheel and scaling to a full-service auto lifestyle consulting firm. Every route, framework, and strategy you'll receive comes from real-world experience, not theory. When you work with ACE, you work with people who've done it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/consultation/intake')} className="bg-red-600 hover:bg-red-700 h-11 px-6 text-sm font-bold" data-testid="cta-start-profile">
              <MapPin className="h-4 w-4 mr-2" /> Start Your Profile
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center h-11 px-6 rounded-md border border-gray-700 text-sm font-bold text-gray-300 hover:bg-gray-900 transition-colors">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DrivingServices;
